import { useRef, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScanLine, Upload, Sparkles } from "lucide-react";

/**
 * OCRUploadDialog — upload an invoice image/PDF and let Claude vision
 * extract the AP-bill fields (vendor, date, amount, category, memo). The
 * onExtracted callback receives the parsed fields so the caller (New Bill
 * dialog) can prefill.
 */
export default function OCRUploadDialog({ onExtracted, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith("image/")) {
      const r = new FileReader();
      r.onload = (ev) => setPreview(ev.target.result);
      r.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const extract = async () => {
    if (!file) return toast.error("Select an invoice image or PDF first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/ocr/invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      const conf = Math.round((data.confidence || 0) * 100);
      toast.success(`Extracted ${data.vendor || "invoice"} — ${conf}% confidence`);
      onExtracted?.(data);
      setOpen(false); setFile(null); setPreview(null);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="ocr-upload-trigger" variant="outline" className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10">
            <ScanLine className="h-4 w-4 mr-2" /> Scan invoice
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-md" data-testid="ocr-dialog">
        <DialogHeader>
          <DialogTitle><ScanLine className="h-4 w-4 inline mr-1 text-indigo-400" /> Scan invoice</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Upload a photo or PDF of the invoice. Claude vision extracts the vendor, amount, date, and category — prefilled into the new-bill form.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-dashed border-white/15 p-4 text-center cursor-pointer hover:bg-white/[0.02]" onClick={() => inputRef.current?.click()} data-testid="ocr-dropzone">
            {preview ? (
              <img src={preview} alt="invoice preview" className="max-h-56 mx-auto rounded" />
            ) : file ? (
              <div className="py-4">
                <Upload className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
                <div className="text-sm">{file.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{file.type || "unknown"}</div>
              </div>
            ) : (
              <div className="py-6">
                <Upload className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
                <div className="text-sm">Click to select an invoice image or PDF</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">PNG · JPG · PDF · up to 12MB</div>
              </div>
            )}
            <input
              ref={inputRef} type="file" accept="image/*,application/pdf"
              className="hidden" onChange={pick} data-testid="ocr-file-input"
            />
          </div>
          <Button onClick={extract} disabled={busy || !file} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="ocr-extract-btn">
            <Sparkles className="h-4 w-4 mr-2" /> {busy ? "Extracting…" : "Extract fields"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
